/**
 * Iter XX.242a · Edit Cricket Graph profile.
 *
 * Small controlled form for the three founder-editable fields:
 *   - Goal
 *   - Current Focus
 *   - Role
 *
 * Historical evidence (baselines, personal bests, missions) is NEVER
 * shown here — the endpoint refuses to modify it and this UI never
 * offers the option. Server-side archival of active missions on
 * goal-change is handled in `cricket_graph.edit_profile`.
 */
import React, { useState } from "react";
import api from "../lib/api";

export default function EditCricketProfile({ initial, onSaved, onCancel }) {
  const [goal, setGoal] = useState(initial?.goal || "");
  const [focus, setFocus] = useState(initial?.current_focus || "");
  const [role, setRole] = useState(initial?.identity?.role || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    if (busy) return;
    setBusy(true); setErr("");
    try {
      const body = {};
      if (goal.trim())  body.goal          = goal.trim();
      if (focus.trim()) body.current_focus = focus.trim();
      if (role.trim())  body.player_role   = role.trim();
      if (Object.keys(body).length === 0) {
        setErr("Nothing to update.");
        setBusy(false);
        return;
      }
      const { data } = await api.post("/cricket-graph/profile", body);
      onSaved && onSaved(data);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't save. Try again.";
      setErr(String(detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="edit-cricket-profile"
      className="rounded-3xl border border-neutral-800 bg-neutral-900/70 p-6 my-6"
    >
      <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-4">
        Edit My Cricket
      </div>

      <label className="block mb-4">
        <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">
          Goal
        </div>
        <input
          data-testid="edit-profile-goal"
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          className="w-full bg-neutral-950 border border-neutral-800 rounded-xl px-3 py-2 text-[15px] text-neutral-100 focus:outline-none focus:border-emerald-800"
          placeholder="e.g. Make School First XI"
          maxLength={240}
        />
      </label>

      <label className="block mb-4">
        <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">
          Current Focus
        </div>
        <input
          data-testid="edit-profile-focus"
          value={focus}
          onChange={(e) => setFocus(e.target.value)}
          className="w-full bg-neutral-950 border border-neutral-800 rounded-xl px-3 py-2 text-[15px] text-neutral-100 focus:outline-none focus:border-emerald-800"
          placeholder="e.g. Bowling Control"
          maxLength={240}
        />
      </label>

      <label className="block mb-5">
        <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">
          Role
        </div>
        <input
          data-testid="edit-profile-role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="w-full bg-neutral-950 border border-neutral-800 rounded-xl px-3 py-2 text-[15px] text-neutral-100 focus:outline-none focus:border-emerald-800"
          placeholder="e.g. off-spinner"
          maxLength={240}
        />
      </label>

      {err && (
        <div data-testid="edit-profile-error" className="text-[13px] text-rose-400 mb-3">
          {err}
        </div>
      )}

      <div className="flex gap-3">
        <button
          data-testid="edit-profile-save"
          onClick={save}
          disabled={busy}
          className="flex-1 py-3 rounded-full bg-emerald-500 text-neutral-950 font-medium hover:bg-emerald-400 transition-colors disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save changes"}
        </button>
        <button
          data-testid="edit-profile-cancel"
          onClick={onCancel}
          disabled={busy}
          className="flex-1 py-3 rounded-full border border-neutral-800 text-neutral-300 hover:bg-neutral-800 transition-colors"
        >
          Cancel
        </button>
      </div>

      <p className="text-[12px] text-neutral-500 mt-4 leading-relaxed">
        Editing your profile changes what Gully focuses on next.
        Past sessions, personal bests and match records stay as they
        were — history is never rewritten.
      </p>
    </div>
  );
}
